// C-04 blocking problems (UserFlows C-04; mockup `SettingsPage.tsx` `validate`). Save stays disabled while any is
// listed. The server checks the same rules (API.md §6.11 issue codes).
import { DAY_NAMES, dayOf, fmtTime, toMinutes, toTime, WEEK_ORDER } from './hours'
import { draftPfWindow, type PfDayWindow } from './pfWindow'
import { pfMessageProblems } from './pfMessages'
import type { DayHours, Settings } from './types'

export const PHONE_RE = /\d{3}.*\d{4}/

export const PROBLEMS = {
  noVisitType: 'Turn on at least one visit type.',
  defaultOff: 'The default visit type must be turned on.',
  noLocation: 'Show at least one location to patients.',
  noProvider: 'Show at least one provider to patients.',
  phone: "Add the clinic's phone number.",
  noOpenDay: 'Open the clinic on at least one day of the week.',
  endBeforeStart: (day: string) => `${day}: closing time must be after opening time.`,
  closedInPf: (day: string) => `${day}: closed in Practice Fusion. Turn this day off.`,
  outsidePf: (day: string, w: PfDayWindow) =>
    `${day}: hours must be inside Practice Fusion's hours (${windowLabel(w)}).`,
}

/** "8:00 AM – 5:00 PM". */
export const windowLabel = (w: PfDayWindow) => `${fmtTime(toTime(w.open))} – ${fmtTime(toTime(w.close))}`

/**
 * D-75: an open day's PF-window problem, if any (`null` window = no limit, see `draftPfWindow`). Shared by C-04's
 * list and C-10's inline errors.
 */
export function pfProblem(
  h: DayHours,
  window: ReturnType<typeof draftPfWindow>,
): { kind: 'closed' } | { kind: 'outside'; window: PfDayWindow } | null {
  if (!h.open || window === null) return null
  const w = window[h.weekday] ?? null
  if (w === null) return { kind: 'closed' }
  if (toMinutes(h.start) < w.open || toMinutes(h.end) > w.close) return { kind: 'outside', window: w }
  return null
}

export function validate(s: Settings): string[] {
  const out: string[] = []
  if (!s.visitTypes.some((v) => v.enabled)) out.push(PROBLEMS.noVisitType)
  // A null default saves (no preselection; task 04 assumption); a default that is off doesn't.
  const def = s.rules.defaultVisitTypeId
  if (def !== null && !s.visitTypes.some((v) => v.id === def && v.enabled)) out.push(PROBLEMS.defaultOff)
  if (!s.locations.some((l) => l.visible)) out.push(PROBLEMS.noLocation)
  if (!s.providers.some((p) => p.visible)) out.push(PROBLEMS.noProvider)
  if (!PHONE_RE.test(s.profile.contact.phone)) out.push(PROBLEMS.phone)
  if (!s.profile.hours.some((h) => h.open)) out.push(PROBLEMS.noOpenDay)
  const bad = WEEK_ORDER.find((d) => {
    const h = dayOf(s.profile.hours, d)
    return h?.open === true && toMinutes(h.end) <= toMinutes(h.start)
  })
  if (bad !== undefined) out.push(PROBLEMS.endBeforeStart(DAY_NAMES[bad]))
  // D-75: the PF window of the draft's shown locations, one problem per day (API.md §6.11).
  const window = draftPfWindow(s)
  for (const d of WEEK_ORDER) {
    const h = dayOf(s.profile.hours, d)
    const p = h ? pfProblem(h, window) : null
    if (p?.kind === 'closed') out.push(PROBLEMS.closedInPf(DAY_NAMES[d]))
    if (p?.kind === 'outside') out.push(PROBLEMS.outsidePf(DAY_NAMES[d], p.window))
  }
  // Task 24 (C-14): unknown `{Field}`s and over-long templates.
  out.push(...pfMessageProblems(s))
  return out
}
